import {
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  VStack,
  Button,
  Box,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  HStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  REGEX_CONTAINS_BOTH_LOWER_AND_UPPERCASE,
  REGEX_HAS_AT_LEAST_ONE_NUMBER,
  REGEX_HAS_SYMBOL,
  lengthInBetween,
  hasLowerAndUpperCase,
  hasSymbol,
  hasDigit,
} from '../../utils/validations';
import { CustomCheckIcon } from '../../theme/icons/CustomCheckIcon';
import { CustomExclamationMarkIcon } from '../../theme/icons/CustomExclamationMarkIcon';
import { CustomVisibilityIcon } from '../../theme/icons/CustomVisibilityIcon';
import { CustomVisibilityOffIcon } from '../../theme/icons/CustomVisibilityOffIcon';
import { useParams } from 'react-router';
import { useContext, useState } from 'react';
import CustomToast from '../../common/CustomToast';
import ErrorToast from '../../common/ErrorToast';
import { getErrorResponsePayload } from '../../utils/ajax';
import { HttpContext } from '../../context/HttpContext';
import { useNavigate } from 'react-router-dom';
import { trackingSearch } from '../../utils/trackingParams';

function ResetPasswordPage() {
  const { resetToken } = useParams();
  const toast = useToast();
  const { publicAxios } = useContext(HttpContext);
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const showPasswordClickHandler = () => setShow(!show);

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required()
      .min(6)
      .max(30)
      .matches(REGEX_CONTAINS_BOTH_LOWER_AND_UPPERCASE)
      .matches(REGEX_HAS_AT_LEAST_ONE_NUMBER)
      .matches(REGEX_HAS_SYMBOL),
    passwordConfirm: Yup.string()
      .required('Please re-enter your password')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
    watch,
    getFieldState,
    setError,
  } = useForm(formOptions);

  const watchPassword = watch('password');
  const fieldStatePassword = getFieldState('password');

  function onSubmit(values) {
    return new Promise((resolve) => {
      const data = {
        plainPassword: values.password,
        token: resetToken,
      };

      publicAxios
        .post(`accounts/me/password/reset`, data)
        .then((res) => {
          const { message } = res.data;
          if (message === 'Password updated successfully') {
            toast({
              render: (props) => (
                <CustomToast
                  status="success"
                  title="Password updated successfully."
                  description="You can now login with your new password."
                  onClose={props.onClose}
                  id={props.id}
                />
              ),
            });
            navigate(`/login${trackingSearch()}`);
          }
        })
        .catch((onError) => {
          console.log(onError);
          const { errors } = getErrorResponsePayload(onError);
          if (errors && errors.plainPassword && errors.plainPassword.errors) {
            const message = errors.plainPassword.errors[0];
            setError('password', { type: 'server', message });
          } else if (errors && errors.token && errors.token.errors) {
            const message = errors.token.errors[0];
            toast({
              render: (props) => (
                <ErrorToast
                  error={onError}
                  description={message || undefined}
                  onClose={props.onClose}
                  id={props.id}
                />
              ),
            });
          }
        });
      resolve();
    });
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} w="100%">
        <VStack spacing={5} alignItems="flex-start">
          <VStack spacing={2} alignItems="flex-start">
            <Text textStyle="heading2">Reset password</Text>
            <Text textStyle="bodyMedium">
              Great passwords use upper &amp; lower case letters, combined with
              numbers and special characters.
            </Text>
          </VStack>
          <FormControl isRequired isInvalid={errors.password}>
            <FormLabel textStyle="bodyMedium" color="#666666">
              New password
            </FormLabel>
            <InputGroup>
              <Input
                id="password"
                type={show ? 'text' : 'password'}
                placeholder="Your password"
                {...register('password')}
                bg="white"
                borderRadius="12px"
                border="1px solid #D0D5DD"
                mt={1}
              />
              <InputRightElement>
                {show ? (
                  <CustomVisibilityOffIcon
                    onClick={showPasswordClickHandler}
                    boxSize="18px"
                    cursor="pointer"
                    color="secondaryDark06"
                  />
                ) : (
                  <CustomVisibilityIcon
                    onClick={showPasswordClickHandler}
                    boxSize="18px"
                    cursor="pointer"
                    color="secondaryDark06"
                  />
                )}
              </InputRightElement>
            </InputGroup>
            {fieldStatePassword.invalid && (
              <FormHelperText mt={2} ml={2}>
                <VStack spacing={1} alignItems="left">
                  <HStack spacing={10} alignItems="left">
                    <Box
                      color={
                        lengthInBetween(watchPassword, 6, 30)
                          ? 'success'
                          : '#989898'
                      }
                    >
                      {lengthInBetween(watchPassword, 6, 30) ? (
                        <CustomCheckIcon boxSize={2} mr={1} />
                      ) : (
                        <CustomExclamationMarkIcon boxSize={2} mr={1} />
                      )}
                      Between 6 and 30 characters
                    </Box>
                    <Box
                      color={
                        hasLowerAndUpperCase(watchPassword)
                          ? 'success'
                          : '#989898'
                      }
                    >
                      {hasLowerAndUpperCase(watchPassword, 6, 30) ? (
                        <CustomCheckIcon boxSize={2} mr={1} />
                      ) : (
                        <CustomExclamationMarkIcon boxSize={2} mr={1} />
                      )}
                      Both lower and upper case letters
                    </Box>
                  </HStack>
                  <Box
                    color={
                      hasDigit(watchPassword) && hasSymbol(watchPassword)
                        ? 'success'
                        : '#989898'
                    }
                  >
                    {hasDigit(watchPassword) && hasSymbol(watchPassword) ? (
                      <CustomCheckIcon boxSize={2} mr={1} />
                    ) : (
                      <CustomExclamationMarkIcon boxSize={2} mr={1} />
                    )}
                    At least one number and a symbol
                  </Box>
                </VStack>
              </FormHelperText>
            )}
          </FormControl>
          <FormControl isRequired isInvalid={errors.passwordConfirm}>
            <FormLabel textStyle="bodyMedium" color="#666666">
              Re-enter new password
            </FormLabel>
            <InputGroup>
              <Input
                id="passwordConfirm"
                type={show ? 'text' : 'password'}
                placeholder="Re-enter your password"
                {...register('passwordConfirm')}
                bg="white"
                borderRadius="12px"
                border="1px solid #D0D5DD"
                mt={1}
              />
              <InputRightElement>
                {show ? (
                  <CustomVisibilityOffIcon
                    onClick={showPasswordClickHandler}
                    boxSize="18px"
                    cursor="pointer"
                    color="secondaryDark06"
                  />
                ) : (
                  <CustomVisibilityIcon
                    onClick={showPasswordClickHandler}
                    boxSize="18px"
                    cursor="pointer"
                    color="secondaryDark06"
                  />
                )}
              </InputRightElement>
            </InputGroup>
            <Box
              mt={2}
              ml={2}
              color="#989898"
              fontSize="12px"
              alignItems="left"
            >
              {errors.passwordConfirm && (
                <CustomExclamationMarkIcon boxSize={2} mr={1} />
              )}
              {errors.passwordConfirm?.message || errors.passwordConfirm}
            </Box>
          </FormControl>
          <Box w="full">
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty || !isValid || isSubmitting}
              mt={4}
              alt="Reset password"
            >
              Reset password
            </Button>
          </Box>
        </VStack>
      </form>
    </>
  );
}

export default ResetPasswordPage;
